import { CourtroomRealTimeSttConfigurationBuilder } from '@ftr/api-regional'
import {
  HostPlatform,
  ProducerCondition,
  ProducerConditionLevel,
  ProducerConditionType,
  ProducerState,
  StreamingMode,
} from '@ftr/contracts/regional-api'
import { randomInt } from '@ftr/contracts/shared/RandomUtils'
import { generateUuid } from '@ftr/contracts/type/shared'
import { ChildLocation, LocationBuilder } from '@ftr/data-location'
import {
  AbstractBuilder,
  TimeframeWithLocalTimesBuilder,
  randomHostName,
  randomIp,
  randomVersion,
} from '@ftr/foundation'
import { CourtroomRealTimeSttConfiguration } from '@ftr/regional-api-contracts'
import { LocalDateTime, LocalTime, ZoneId, ZonedDateTime } from '@js-joda/core'
import { ProducerDetailWithLocation } from '../api'
import {
  PLENTY_OF_RECORDING_REMAINING_HOURS,
  RECORDING_REMAINING_HOURS_ERROR,
  RECORDING_REMAINING_HOURS_WARNING,
} from '../conditions'
import { AudioDeviceBuilder } from './AudioDeviceBuilder'
import { HostSystemBuilder } from './HostSystemBuilder'
import { HostVideoBuilder } from './HostVideoBuilder'
import { HostVideoDeviceBuilder } from './HostVideoDeviceBuilder'

export class ProducerDetailWithLocationBuilder extends AbstractBuilder<ProducerDetailWithLocation> {
  constructor(readonly repeatable: boolean = false) {
    const location = new LocationBuilder(repeatable).withParent().build() as ChildLocation
    const outputAudioDevice = new AudioDeviceBuilder()
      .with({
        outputs: 2,
        name: 'Test Output Audio Device',
        inputs: 0,
      })
      .build()
    super({
      recordingRemainingSpaceBytes: undefined,
      recordingRemainingTimeSeconds: undefined,
      configuredAudioDevice: new AudioDeviceBuilder().build(),
      configuredOutputAudioDevice: outputAudioDevice,
      availableAudioDevices: [new AudioDeviceBuilder().build()],
      availableVideoDevices: [new HostVideoDeviceBuilder().build()],
      availableOutputAudioDevices: [outputAudioDevice],
      hostname: repeatable ? 'DESKTOP_ABC' : randomHostName(),
      id: repeatable ? '112d29d9-3438-4ca0-b514-a106a1530ae0' : generateUuid(),
      ip: repeatable ? '1.1.1.1' : randomIp(),
      locationId: location.id,
      courtroomName: location.name,
      courthouseName: location.parent.name,
      state: ProducerState.InSession,
      version: repeatable ? '1.5.22' : randomVersion(),
      videoInputs: repeatable ? 0 : randomInt(0, 3),
      enabled: false,
      isCourtroomArchived: false,
      location,
      timezone: ZoneId.systemDefault(),
      courthouseTimezone: ZoneId.systemDefault(),
      mode: StreamingMode.Traditional,
      streamStartedAt: LocalTime.of(12),
      // Below fields only populated for detail, not list
      departmentName: undefined,
      courtroomSttConfiguration: undefined,
    })
  }

  withDepartment(): this {
    return this.with({
      departmentName: 'Department of Justice',
    }).withDepartmentWeeklyStreamLocationConfiguration()
  }

  withCourtroomSttConfiguration(courtroomSttConfiguration?: CourtroomRealTimeSttConfiguration): this {
    return this.with({
      courtroomSttConfiguration: courtroomSttConfiguration || new CourtroomRealTimeSttConfigurationBuilder().build(),
    })
  }

  withSystem(platform: HostPlatform = HostPlatform.Windows): this {
    return this.with({
      system: new HostSystemBuilder(platform).build(),
    })
  }

  recording(): this {
    return this.with({
      recordingId: generateUuid(),
    })
  }

  recordingLocally(time: LocalTime): this {
    return this.with({
      localRecordingStartedAt: time,
    })
  }

  withCondition(condition: ProducerCondition): this {
    return this.with({
      conditions: [...(this.result.conditions ?? []), condition],
    })
  }

  withVideoDevices(): this {
    return this.with({
      videoInputs: 3,
      configuredVideoDevices: [
        new HostVideoBuilder(this.repeatable).build(),
        new HostVideoBuilder(this.repeatable).build(),
        new HostVideoBuilder(this.repeatable).build(),
      ],
    })
  }

  withOnlyOneVideoDevice(): this {
    return this.with({
      videoInputs: 1,
      configuredVideoDevices: [new HostVideoBuilder(this.repeatable).build()],
    })
  }

  withOneDisconnectedVideoDevice(): this {
    return this.with({
      videoInputs: 3,
      configuredVideoDevices: [
        new HostVideoBuilder(this.repeatable).build(),
        new HostVideoBuilder(this.repeatable).withDisconnectedDevice().build(),
        new HostVideoBuilder(this.repeatable).build(),
      ],
    })
  }

  new(): this {
    return this.with({
      location: undefined,
      locationId: undefined,
      state: ProducerState.New,
      streamStartedAt: undefined,
    })
  }

  noAudioDevice(): this {
    return this.with({
      state: ProducerState.Error,
    }).withCondition({
      type: ProducerConditionType.AudioDeviceUnavailable,
      detail: 'The selected audio device is not available',
      level: ProducerConditionLevel.Fatal,
      technicalDetail: 'Never gunna give you up, never gunna let you down, Never gonna run around and desert you.',
      helpUrl: 'https://www.youtube.com/watch?v=dQw4w9WgXcQ',
    })
  }

  missingVideoDevice(): this {
    return this.withCondition({
      type: ProducerConditionType.VideoDeviceUnavailable,
      detail: 'Some video device is not available',
      level: ProducerConditionLevel.Warning,
      technicalDetail: null,
      helpUrl: null,
    })
  }

  streamingServerError(): this {
    return this.withCondition({
      type: ProducerConditionType.StreamingServerError,
      detail: 'The server broke son',
      level: ProducerConditionLevel.Error,
      technicalDetail: null,
      helpUrl: null,
    })
  }

  crashLoop(): this {
    return this.withCondition({
      type: ProducerConditionType.CrashLoop,
      detail: 'The server is crash looping',
      level: ProducerConditionLevel.Error,
      technicalDetail: null,
      helpUrl: null,
    })
  }

  mismatchTimezone(): this {
    return this.withCondition({
      type: ProducerConditionType.MismatchedTimezone,
      detail:
        'The Recorder is not in the same timezone as the courtroom, you should really fix up the timezone' +
        ' issues and make them better, this warning is intentionally really long just to see what happens in the ui.',
      level: ProducerConditionLevel.Warning,
      technicalDetail: null,
      helpUrl: null,
    })
  }

  clockNotInSync(): this {
    return this.withCondition({
      type: ProducerConditionType.ClockNotInSync,
      detail: 'The Clock is out of sync',
      level: ProducerConditionLevel.Warning,
      technicalDetail: null,
      helpUrl: 'https://www.youtube.com/watch?v=a7BvgNanZDo',
    })
  }

  notHeartbeating(): this {
    return this.withCondition({
      type: ProducerConditionType.NotHeartbeating,
      detail: 'We have not received a heartbeat recently',
      level: ProducerConditionLevel.Info,
      technicalDetail: null,
      helpUrl: null,
    })
  }

  needsUpdate(): this {
    return this.with({
      version: '14.0.0',
      upgradeVersionRange: {
        expected: '14.0.1',
        minimum: '13.9.9',
      },
    })
  }

  notRecording(): this {
    return this.with({
      state: ProducerState.NotRecording,
      streamStartedAt: undefined,
    })
  }
  error(): this {
    return this.with({
      state: ProducerState.Error,
      streamStartedAt: undefined,
    })
  }
  starting(): this {
    return this.with({
      state: ProducerState.Starting,
      streamStartedAt: undefined,
    })
  }

  notInSession(): this {
    return this.with({
      state: ProducerState.NotInSession,
      streamStartedAt: LocalTime.of(12),
    })
  }
  configuring(): this {
    return this.with({
      state: ProducerState.Configuring,
      streamStartedAt: undefined,
    })
  }

  off(): this {
    return this.with({
      state: ProducerState.Disabled,
      streamStartedAt: undefined,
    })
  }

  disconnected(recently: boolean = false): this {
    return this.with({
      state: ProducerState.Disconnected,
      lastHeartbeatClientTime: recently ? LocalDateTime.now() : LocalDateTime.now().minusDays(1),
      streamStartedAt: undefined,
    })
  }

  gbToBytes(gb: number): number {
    return 1024 * 1024 * 1024 * gb
  }

  hoursToSeconds(hours: number): number {
    return 60 * 60 * hours
  }

  withPlentyRemainingSpace(): this {
    return this.with({
      recordingRemainingSpaceBytes: this.gbToBytes(600),
      recordingRemainingTimeSeconds: this.hoursToSeconds(PLENTY_OF_RECORDING_REMAINING_HOURS) + 1,
    })
  }

  withLowRemainingSpace(): this {
    return this.with({
      recordingRemainingSpaceBytes: this.gbToBytes(7),
      recordingRemainingTimeSeconds: this.hoursToSeconds(RECORDING_REMAINING_HOURS_WARNING) - 1,
    })
  }

  withNoRemainingSpace(): this {
    return this.with({
      recordingRemainingSpaceBytes: this.gbToBytes(1),
      recordingRemainingTimeSeconds: this.hoursToSeconds(RECORDING_REMAINING_HOURS_ERROR) - 1,
    })
  }

  withWeeklyStreamLocationConfiguration(): this {
    return this.with({
      weeklyStreamLocationConfiguration: {
        monday: {
          schedule: new TimeframeWithLocalTimesBuilder('09:00', '17:00').build(),
          onTheRecordSchedules: [
            new TimeframeWithLocalTimesBuilder('09:00', '10:00').build(),
            new TimeframeWithLocalTimesBuilder('10:15', '11:00').build(),
            new TimeframeWithLocalTimesBuilder('13:00', '15:00').build(),
            new TimeframeWithLocalTimesBuilder('15:15', '16:45').build(),
          ],
        },
        tuesday: {
          schedule: new TimeframeWithLocalTimesBuilder('09:00', '16:00').build(),
          onTheRecordSchedules: [new TimeframeWithLocalTimesBuilder().build()],
        },
        wednesday: {
          schedule: new TimeframeWithLocalTimesBuilder('09:00', '16:10').build(),
          onTheRecordSchedules: [new TimeframeWithLocalTimesBuilder().build()],
        },
        thursday: {
          schedule: new TimeframeWithLocalTimesBuilder('09:00', '16:30').build(),
          onTheRecordSchedules: [new TimeframeWithLocalTimesBuilder().build()],
        },
        friday: {
          schedule: new TimeframeWithLocalTimesBuilder('09:00', '16:40').build(),
          onTheRecordSchedules: [new TimeframeWithLocalTimesBuilder().build()],
        },
        saturday: {},
        sunday: {},
      },
    })
  }

  withDepartmentWeeklyStreamLocationConfiguration(): this {
    const departmentId = generateUuid()
    return this.with({
      weeklyStreamLocationConfiguration: {
        monday: {
          departmentId,
          schedule: new TimeframeWithLocalTimesBuilder('09:00', '17:00').build(),
          onTheRecordSchedules: [
            new TimeframeWithLocalTimesBuilder('09:00', '10:00').build(),
            new TimeframeWithLocalTimesBuilder('10:15', '11:00').build(),
            new TimeframeWithLocalTimesBuilder('13:00', '15:00').build(),
            new TimeframeWithLocalTimesBuilder('15:15', '17:00').build(),
          ],
        },
        tuesday: {
          schedule: new TimeframeWithLocalTimesBuilder('09:00', '16:00').build(),
          onTheRecordSchedules: [new TimeframeWithLocalTimesBuilder().build()],
          departmentId,
        },
        wednesday: {
          schedule: new TimeframeWithLocalTimesBuilder('09:00', '16:10').build(),
          onTheRecordSchedules: [new TimeframeWithLocalTimesBuilder().build()],
          departmentId,
        },
        thursday: {
          schedule: new TimeframeWithLocalTimesBuilder('09:00', '16:30').build(),
          onTheRecordSchedules: [new TimeframeWithLocalTimesBuilder().build()],
          departmentId,
        },
        friday: {
          schedule: new TimeframeWithLocalTimesBuilder('09:00', '16:40').build(),
          onTheRecordSchedules: [new TimeframeWithLocalTimesBuilder().build()],
          departmentId,
        },
        saturday: {},
        sunday: {},
      },
    })
  }

  endOfLifeDate(date: ZonedDateTime): this {
    return this.with({
      endOfLife: date,
    })
  }
}
